import { useState } from "react";
import { z } from "zod";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";
import type { connectorTypes } from "@pineseed/spec";
import { v7 as uuidv7 } from "uuid";

type Channel = connectorTypes["Channel"];
type Provider = connectorTypes["Provider"];
type Connection = connectorTypes["Connection"];
type Invitation = connectorTypes["Invitation"];
type ThreadMapping = connectorTypes["ThreadMapping"];

// ── Zod schemas for forms ──────────────────────────────────────────────────

const CreateChannelSchema = z.object({
  name: z.string().min(1).describe("Channel name"),
  type: z.string().min(1).describe("Channel type (e.g. phone, whatsapp, email)"),
});

const UpdateChannelSchema = z.object({
  name: z.string().min(1).optional().describe("Channel name"),
  type: z.string().min(1).optional().describe("Channel type"),
});

const CreateProviderSchema = z.object({
  name: z.string().min(1).describe("Provider name"),
  type: z.string().min(1).describe("Provider type (e.g. twilio, cloudflare)"),
});

const UpdateProviderSchema = z.object({
  name: z.string().min(1).optional().describe("Provider name"),
  type: z.string().min(1).optional().describe("Provider type"),
});

const CreateConnectionSchema = z.object({
  channelId: z.uuid().describe("Channel"),
  providerId: z.uuid().describe("Provider"),
  name: z.string().min(1).describe("Connection name"),
});

const UpdateConnectionSchema = z.object({
  name: z.string().min(1).describe("Connection name"),
});

const CreateInvitationSchema = z.object({
  channelId: z.uuid().describe("Channel"),
  conversationId: z.uuid().describe("Conversation ID"),
  fromParticipantId: z.string().min(1).describe("From participant ID"),
  createdByParticipantId: z.string().min(1).describe("Created by participant ID"),
  expiresAt: z.string().min(1).describe("Expiration (ISO timestamp)"),
  toExternalThreadId: z.string().optional().describe("External thread ID (optional)"),
});

// CreateThreadMappingSchema removed — replaced by custom form with auto-built external IDs

const CreateAddressMappingSchema = z.object({
  address: z.string().min(1).describe("External address (phone number or email)"),
  channelId: z.uuid().describe("Channel"),
});

// ── Section component ──────────────────────────────────────────────────────

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
      >
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {title} {count !== undefined && <span className="text-gray-500 dark:text-gray-400 font-normal">({count})</span>}
        </span>
        <span className="text-gray-400">{open ? '▼' : '▶'}</span>
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

const CreateThreadMappingSchema = z.object({
  channelId: z.uuid().describe("Channel"),
  internalThreadId: z.uuid().describe("Internal Thread ID"),
  fromParticipantId: z.string().min(1).describe("From Participant ID"),
  createdByParticipantId: z.string().min(1).describe("Created By Participant ID"),
});

// ── Main panel ─────────────────────────────────────────────────────────────

export function ConnectorPanel() {
  const { adminApp, account, connector } = useAdmin();
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // ── Form state ───────────────────────────────────────────────────────────
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [showCreateProvider, setShowCreateProvider] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [showCreateConnection, setShowCreateConnection] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [showCreateInvitation, setShowCreateInvitation] = useState(false);
  const [showCreateThreadMapping, setShowCreateThreadMapping] = useState(false);
  const [showCreateAddressMapping, setShowCreateAddressMapping] = useState(false);

  // ── Invitation/ThreadMapping channel selection ───────────────────────────
  const [invitationChannelId, setInvitationChannelId] = useState('');
  const [threadMappingChannelId, setThreadMappingChannelId] = useState('');

  // ── Column definitions ───────────────────────────────────────────────────

  const channelColumns: Column<Record<string, unknown>>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'id', label: 'ID', render: (item) => (
      <CopyableText id={item.id as string} onCopy={copyToClipboard} isPulsing={isCopied(item.id as string)} variant="muted">
        {(item.id as string).slice(0, 8)}...
      </CopyableText>
    )},
    { key: 'createdAt', label: 'Created', render: (item) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {new Date(item.createdAt as string).toLocaleDateString()}
      </span>
    )},
  ];

  const providerColumns: Column<Record<string, unknown>>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'id', label: 'ID', render: (item) => (
      <CopyableText id={item.id as string} onCopy={copyToClipboard} isPulsing={isCopied(item.id as string)} variant="muted">
        {(item.id as string).slice(0, 8)}...
      </CopyableText>
    )},
    { key: 'createdAt', label: 'Created', render: (item) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {new Date(item.createdAt as string).toLocaleDateString()}
      </span>
    )},
  ];

  const connectionColumns: Column<Record<string, unknown>>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'channelId', label: 'Channel', render: (item) => {
      const ch = connector.channels.find((c: Channel) => c.id === item.channelId);
      return ch ? `${ch.name} (${ch.type})` : (item.channelId as string).slice(0, 8) + '...';
    }},
    { key: 'providerId', label: 'Provider', render: (item) => {
      const p = connector.providers.find((pr: Provider) => pr.id === item.providerId);
      return p ? `${p.name} (${p.type})` : (item.providerId as string).slice(0, 8) + '...';
    }},
    { key: 'id', label: 'ID', render: (item) => (
      <CopyableText id={item.id as string} onCopy={copyToClipboard} isPulsing={isCopied(item.id as string)} variant="muted">
        {(item.id as string).slice(0, 8)}...
      </CopyableText>
    )},
  ];

  const invitationColumns: Column<Record<string, unknown>>[] = [
    { key: 'code', label: 'Code', render: (item) => (
      <CopyableText id={item.code as string} onCopy={copyToClipboard} isPulsing={isCopied(item.code as string)} variant="muted">
        {item.code as string}
      </CopyableText>
    )},
    { key: 'status', label: 'Status', sortable: true },
    { key: 'fromParticipantId', label: 'From', render: (item) => (item.fromParticipantId as string).slice(0, 12) + '...' },
    { key: 'expiresAt', label: 'Expires', render: (item) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {new Date(item.expiresAt as string).toLocaleString()}
      </span>
    )},
    { key: 'id', label: 'ID', render: (item) => (
      <CopyableText id={item.id as string} onCopy={copyToClipboard} isPulsing={isCopied(item.id as string)} variant="muted">
        {(item.id as string).slice(0, 8)}...
      </CopyableText>
    )},
  ];

  const threadMappingColumns: Column<Record<string, unknown>>[] = [
    { key: 'externalThreadId', label: 'External Thread', render: (item) => (
      <CopyableText id={item.externalThreadId as string} onCopy={copyToClipboard} isPulsing={isCopied(item.externalThreadId as string)} variant="muted">
        {(item.externalThreadId as string).slice(0, 20)}...
      </CopyableText>
    )},
    { key: 'internalThreadId', label: 'Internal Thread', render: (item) => (
      <CopyableText id={item.internalThreadId as string} onCopy={copyToClipboard} isPulsing={isCopied(item.internalThreadId as string)} variant="muted">
        {(item.internalThreadId as string).slice(0, 8)}...
      </CopyableText>
    )},
    { key: 'channelId', label: 'Channel', render: (item) => {
      const ch = connector.channels.find((c: Channel) => c.id === item.channelId);
      return ch ? `${ch.name} (${ch.type})` : (item.channelId as string).slice(0, 8) + '...';
    }},
    { key: 'createdAt', label: 'Created', render: (item) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {new Date(item.createdAt as string).toLocaleDateString()}
      </span>
    )},
  ];

  const addressMappingColumns: Column<Record<string, unknown>>[] = [
    { key: 'address', label: 'Address', sortable: true },
    { key: 'channelId', label: 'Channel', render: (item) => {
      const ch = connector.channels.find((c: Channel) => c.id === item.channelId);
      return ch ? `${ch.name} (${ch.type})` : (item.channelId as string).slice(0, 8) + '...';
    }},
    { key: 'workspaceId', label: 'Workspace', render: (item) => (
      <CopyableText id={item.workspaceId as string} onCopy={copyToClipboard} isPulsing={isCopied(item.workspaceId as string)} variant="muted">
        {(item.workspaceId as string).slice(0, 8)}...
      </CopyableText>
    )},

    { key: 'createdAt', label: 'Created', render: (item) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {new Date(item.createdAt as string).toLocaleDateString()}
      </span>
    )},
  ];

  const threadRouteColumns: Column<Record<string, unknown>>[] = [
    { key: 'externalThreadId', label: 'External Thread ID', sortable: true },
    { key: 'channelId', label: 'Channel', render: (item) => {
      const ch = connector.channels.find((c: Channel) => c.id === item.channelId);
      return ch ? `${ch.name} (${ch.type})` : (item.channelId as string).slice(0, 8) + '...';
    }},
    { key: 'workspaceId', label: 'Workspace', render: (item) => (
      <CopyableText id={item.workspaceId as string} onCopy={copyToClipboard} isPulsing={isCopied(item.workspaceId as string)} variant="muted">
        {(item.workspaceId as string).slice(0, 8)}...
      </CopyableText>
    )},
    { key: 'createdAt', label: 'Created', render: (item) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {new Date(item.createdAt as string).toLocaleDateString()}
      </span>
    )},
  ];

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCreateChannel = async (data: Record<string, unknown>) => {
    await connector.createChannel({ organizationId: connector.selectedOrgId, name: data.name as string, type: data.type as string });
    setShowCreateChannel(false);
  };

  const handleEditChannel = async (data: Record<string, unknown>) => {
    if (!editingChannel) return;
    await connector.updateChannel(editingChannel.id, { name: data.name as string, type: data.type as string });
    setEditingChannel(null);
  };

  const handleCreateProvider = async (data: Record<string, unknown>) => {
    await connector.createProvider({ organizationId: connector.selectedOrgId, name: data.name as string, type: data.type as string });
    setShowCreateProvider(false);
  };

  const handleEditProvider = async (data: Record<string, unknown>) => {
    if (!editingProvider) return;
    await connector.updateProvider(editingProvider.id, { name: data.name as string, type: data.type as string });
    setEditingProvider(null);
  };

  const handleCreateConnection = async (data: Record<string, unknown>) => {
    await connector.createConnection({
      workspaceId: adminApp.workspaceId,
      channelId: data.channelId as string,
      providerId: data.providerId as string,
      name: data.name as string,
    });
    setShowCreateConnection(false);
  };

  const handleEditConnection = async (data: Record<string, unknown>) => {
    if (!editingConnection) return;
    await connector.updateConnection(editingConnection.id, { name: data.name as string });
    setEditingConnection(null);
  };

  const handleCreateThreadMapping = async (data: Record<string, unknown>) => {
    if (threadMappingTypeError) {
      throw new Error(threadMappingTypeError);
    }
    if (!computedExternalThreadId || !computedExternalParticipantId) {
      throw new Error("Enter their address(es) above to generate external IDs");
    }
    await connector.createThreadMapping({
      channelId: data.channelId as string,
      internalThreadId: data.internalThreadId as string,
      externalThreadId: computedExternalThreadId,
      externalParticipantId: computedExternalParticipantId,
      fromParticipantId: data.fromParticipantId as string,
      createdByParticipantId: data.createdByParticipantId as string,
    });
    setShowCreateThreadMapping(false);
  };

  // Address builder: compute external IDs from channel + address mapping + participant addresses
  const [theirAddresses, setTheirAddresses] = useState('');
  const threadMappingChannel = connector.channels.find(ch => ch.id === threadMappingChannelId);
  const [ourAddress, setOurAddress] = useState('');
  const providerAddress = ourAddress;
  const theirList = theirAddresses.split(',').map(a => a.trim()).filter(Boolean);
  const allSorted = providerAddress && theirList.length > 0
    ? [providerAddress, ...theirList].sort().join(',')
    : '';
  // Composed type `${channel.type}_${provider.type}` via the channel's single connection (mirrors backend getChannelType)
  const threadMappingConnections = connector.connections.filter((cn: Connection) => cn.channelId === threadMappingChannelId);
  const threadMappingConnection = threadMappingConnections.length === 1 ? threadMappingConnections[0] : undefined;
  const threadMappingProvider = threadMappingConnection
    ? connector.providers.find((p: Provider) => p.id === threadMappingConnection.providerId)
    : undefined;
  const threadMappingTypeError = !threadMappingChannel
    ? null
    : !threadMappingConnection
      ? `Channel requires exactly one connection (found ${threadMappingConnections.length})`
      : !threadMappingProvider
        ? `Provider not found: ${threadMappingConnection.providerId}`
        : null;
  const composedChannelType = threadMappingChannel && threadMappingProvider
    ? `${threadMappingChannel.type}_${threadMappingProvider.type}`
    : null;
  const computedExternalThreadId = composedChannelType && allSorted
    ? `${composedChannelType}:${providerAddress}:${allSorted}`
    : '';
  const computedExternalParticipantId = computedExternalThreadId && theirList[0]
    ? `${computedExternalThreadId}:${theirList[0]}`
    : '';

  const handleCreateAddressMapping = async (data: Record<string, unknown>) => {
    await connector.createAddressMapping({
      address: data.address as string,
      channelId: data.channelId as string,
      workspaceId: adminApp.workspaceId,
    });
    setShowCreateAddressMapping(false);
  };

  const handleCreateInvitation = async (data: Record<string, unknown>) => {
    await connector.createInvitation({
      id: uuidv7(),
      channelId: data.channelId as string,
      conversationId: data.conversationId as string,
      fromParticipantId: data.fromParticipantId as string,
      createdByParticipantId: data.createdByParticipantId as string,
      expiresAt: data.expiresAt as string,
      toExternalThreadId: data.toExternalThreadId as string | undefined,
    });
    setShowCreateInvitation(false);
  };


  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full">
      {/* ── Account / Org Selectors ─────────────────────────────────── */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account</label>
          <select
            value={connector.selectedAccountId}
            onChange={(e) => connector.setSelectedAccountId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select account...</option>
            {account.accounts.entities.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization</label>
          <select
            value={connector.selectedOrgId}
            onChange={(e) => connector.setSelectedOrgId(e.target.value)}
            disabled={!connector.selectedAccountId}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          >
            <option value="">Select organization...</option>
            {account.organizations.entities.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Channels ────────────────────────────────────────────────── */}
      <Section title="Channels" count={connector.channels.length}>
        <div className="mb-2">
          <button onClick={() => setShowCreateChannel(true)} disabled={!connector.selectedOrgId}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            + Channel
          </button>
        </div>
        {connector.channelsError && <p className="text-red-500 text-sm mb-2">{connector.channelsError}</p>}
        <EntityTable
          data={connector.channels as unknown as Record<string, unknown>[]}
          columns={channelColumns}
          onEdit={(item) => setEditingChannel(item as unknown as Channel)}
          onDelete={(item) => connector.deleteChannel((item as unknown as Channel).id)}
          loading={connector.channelsLoading}
          emptyMessage={connector.selectedOrgId ? "No channels" : "Select an organization"}
        />
      </Section>

      {/* ── Providers ───────────────────────────────────────────────── */}
      <Section title="Providers" count={connector.providers.length}>
        <div className="mb-2">
          <button onClick={() => setShowCreateProvider(true)} disabled={!connector.selectedOrgId}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            + Provider
          </button>
        </div>
        {connector.providersError && <p className="text-red-500 text-sm mb-2">{connector.providersError}</p>}
        <EntityTable
          data={connector.providers as unknown as Record<string, unknown>[]}
          columns={providerColumns}
          onEdit={(item) => setEditingProvider(item as unknown as Provider)}
          onDelete={(item) => connector.deleteProvider((item as unknown as Provider).id)}
          loading={connector.providersLoading}
          emptyMessage={connector.selectedOrgId ? "No providers" : "Select an organization"}
        />
      </Section>

      {/* ── Connections ──────────────────────────────────────────────── */}
      <Section title="Connections" count={connector.connections.length}>
        <div className="mb-2">
          <button onClick={() => setShowCreateConnection(true)} disabled={!adminApp.workspaceId}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            + Connection
          </button>
        </div>
        {connector.connectionsError && <p className="text-red-500 text-sm mb-2">{connector.connectionsError}</p>}
        <EntityTable
          data={connector.connections as unknown as Record<string, unknown>[]}
          columns={connectionColumns}
          onEdit={(item) => setEditingConnection(item as unknown as Connection)}
          onDelete={(item) => connector.deleteConnection((item as unknown as Connection).id)}
          loading={connector.connectionsLoading}
          emptyMessage={adminApp.workspaceId ? "No connections" : "Set a workspace ID"}
        />
      </Section>

      {/* ── Invitations ──────────────────────────────────────────────── */}
      <Section title="Invitations" count={connector.invitations.length}>
        <div className="flex gap-2 mb-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Channel</label>
            <select value={invitationChannelId} onChange={(e) => { setInvitationChannelId(e.target.value); if (e.target.value) connector.loadInvitations(e.target.value); }}
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
              <option value="">Select channel...</option>
              {connector.channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name} ({ch.type})</option>)}
            </select>
          </div>
          <button onClick={() => setShowCreateInvitation(true)} disabled={!invitationChannelId}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            + Invitation
          </button>
        </div>
        {connector.invitationsError && <p className="text-red-500 text-sm mb-2">{connector.invitationsError}</p>}
        <EntityTable
          data={connector.invitations as unknown as Record<string, unknown>[]}
          columns={invitationColumns}
          onDelete={(item) => connector.revokeInvitation((item as unknown as Invitation).id)}
          loading={connector.invitationsLoading}
          emptyMessage={invitationChannelId ? "No invitations" : "Select a channel"}
        />
      </Section>

      {/* ── Thread Mappings ──────────────────────────────────────────── */}
      <Section title="Thread Mappings" count={connector.threadMappings.length}>
        <div className="flex gap-2 mb-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Channel</label>
            <select value={threadMappingChannelId} onChange={(e) => { setThreadMappingChannelId(e.target.value); if (e.target.value) connector.loadThreadMappings(e.target.value); }}
            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="">Select channel...</option>
              {connector.channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name} ({ch.type})</option>)}
            </select>
          </div>
          <button onClick={() => setShowCreateThreadMapping(true)} disabled={!threadMappingChannelId}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            + Mapping
          </button>
        </div>
        {connector.threadMappingsError && <p className="text-red-500 text-sm mb-2">{connector.threadMappingsError}</p>}
        <EntityTable
          data={connector.threadMappings as unknown as Record<string, unknown>[]}
          columns={threadMappingColumns}
          onDelete={(item) => {
            const tm = item as unknown as ThreadMapping;
            connector.deleteThreadMapping(tm.externalThreadId, tm.channelId);
          }}
          loading={connector.threadMappingsLoading}
          emptyMessage={threadMappingChannelId ? "No thread mappings" : "Select a channel"}
        />
      </Section>

      {/* ── Address Mappings ─────────────────────────────────────────── */}
      <Section title="Address Mappings" count={connector.addressMappings.length}>
        <div className="flex gap-2 mb-2 items-end">
          <button onClick={() => connector.loadAddressMappings()}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700">
            Load
          </button>
          <button onClick={() => setShowCreateAddressMapping(true)}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
            + Mapping
          </button>
        </div>
        {connector.addressMappingsError && <p className="text-red-500 text-sm mb-2">{connector.addressMappingsError}</p>}
        <EntityTable
          data={connector.addressMappings as unknown as Record<string, unknown>[]}
          columns={addressMappingColumns}
          onDelete={(item) => connector.deleteAddressMapping(item.address as string)}
          loading={connector.addressMappingsLoading}
          emptyMessage="No address mappings"
        />
      </Section>

      {/* ── Thread Routes (read-only) ────────────────────────────────── */}
      <Section title="Thread Routes" count={connector.threadRoutes.length}>
        <div className="flex gap-2 mb-2 items-end">
          <button onClick={() => connector.loadThreadRoutes()}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700">
            Load
          </button>
        </div>
        {connector.threadRoutesError && <p className="text-red-500 text-sm mb-2">{connector.threadRoutesError}</p>}
        <EntityTable
          data={connector.threadRoutes as unknown as Record<string, unknown>[]}
          columns={threadRouteColumns}
          loading={connector.threadRoutesLoading}
          emptyMessage="No thread routes"
        />
      </Section>


      {/* ── Modals ───────────────────────────────────────────────────── */}

      <Modal isOpen={showCreateChannel} onClose={() => setShowCreateChannel(false)} title="Create Channel">
        <EntityForm schema={CreateChannelSchema} onSubmit={handleCreateChannel as any} onCancel={() => setShowCreateChannel(false)} submitLabel="Create" />
      </Modal>

      <Modal isOpen={!!editingChannel} onClose={() => setEditingChannel(null)} title="Edit Channel">
        {editingChannel && (
          <EntityForm schema={UpdateChannelSchema} initialData={editingChannel as any} onSubmit={handleEditChannel as any} onCancel={() => setEditingChannel(null)} submitLabel="Save" />
        )}
      </Modal>

      <Modal isOpen={showCreateProvider} onClose={() => setShowCreateProvider(false)} title="Create Provider">
        <EntityForm schema={CreateProviderSchema} onSubmit={handleCreateProvider as any} onCancel={() => setShowCreateProvider(false)} submitLabel="Create" />
      </Modal>

      <Modal isOpen={!!editingProvider} onClose={() => setEditingProvider(null)} title="Edit Provider">
        {editingProvider && (
          <EntityForm schema={UpdateProviderSchema} initialData={editingProvider as any} onSubmit={handleEditProvider as any} onCancel={() => setEditingProvider(null)} submitLabel="Save" />
        )}
      </Modal>

      <Modal isOpen={showCreateConnection} onClose={() => setShowCreateConnection(false)} title="Create Connection">
        <EntityForm
          schema={CreateConnectionSchema}
          onSubmit={handleCreateConnection as any}
          onCancel={() => setShowCreateConnection(false)}
          submitLabel="Create"
          customOptions={{
            channelId: connector.channels.map(ch => ({ id: ch.id, label: `${ch.name} (${ch.type})` })),
            providerId: connector.providers.map(p => ({ id: p.id, label: `${p.name} (${p.type})` })),
          }}
        />
      </Modal>

      <Modal isOpen={!!editingConnection} onClose={() => setEditingConnection(null)} title="Edit Connection">
        {editingConnection && (
          <EntityForm schema={UpdateConnectionSchema} initialData={editingConnection as any} onSubmit={handleEditConnection as any} onCancel={() => setEditingConnection(null)} submitLabel="Save" />
        )}
      </Modal>

      <Modal isOpen={showCreateInvitation} onClose={() => setShowCreateInvitation(false)} title="Create Invitation">
        <EntityForm
          schema={CreateInvitationSchema}
          initialData={{ channelId: invitationChannelId } as any}
          onSubmit={handleCreateInvitation as any}
          onCancel={() => setShowCreateInvitation(false)}
          submitLabel="Create"
          customOptions={{
            channelId: connector.channels.map(ch => ({ id: ch.id, label: `${ch.name} (${ch.type})` })),
          }}
        />
      </Modal>

      <Modal isOpen={showCreateThreadMapping} onClose={() => setShowCreateThreadMapping(false)} title="Create Thread Mapping">
        <div className="px-4 pt-2 pb-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Our address</label>
            <input
              type="text"
              value={ourAddress}
              onChange={e => setOurAddress(e.target.value)}
              placeholder={threadMappingChannel?.type?.startsWith('phone') ? '+18555213502' : 'support@yourdomain.com'}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Their address(es) <span className="text-gray-400 font-normal">(comma-separated)</span>
            </label>
            <input
              type="text"
              value={theirAddresses}
              onChange={e => setTheirAddresses(e.target.value)}
              placeholder={threadMappingChannel?.type?.startsWith('phone') ? '+16507048224' : 'bob@example.com'}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>
          {threadMappingTypeError && (
            <div className="text-xs text-red-600 dark:text-red-400">{threadMappingTypeError}</div>
          )}
          {computedExternalThreadId && (
            <div className="text-xs font-mono text-gray-500 break-all">Thread: {computedExternalThreadId}</div>
          )}
          {computedExternalParticipantId && (
            <div className="text-xs font-mono text-gray-500 break-all">Participant: {computedExternalParticipantId}</div>
          )}
        </div>
        <EntityForm
          schema={CreateThreadMappingSchema}
          initialData={{ channelId: threadMappingChannelId } as any}
          onSubmit={handleCreateThreadMapping as any}
          onCancel={() => setShowCreateThreadMapping(false)}
          submitLabel="Create"
          customOptions={{
            channelId: connector.channels.map(ch => ({ id: ch.id, label: `${ch.name} (${ch.type})` })),
          }}
        />
      </Modal>

      <Modal isOpen={showCreateAddressMapping} onClose={() => setShowCreateAddressMapping(false)} title="Create Address Mapping">
        <EntityForm
          schema={CreateAddressMappingSchema}
          initialData={{ workspaceId: adminApp.workspaceId } as any}
          onSubmit={handleCreateAddressMapping as any}
          onCancel={() => setShowCreateAddressMapping(false)}
          submitLabel="Create"
          customOptions={{
            channelId: connector.channels.map(ch => ({ id: ch.id, label: `${ch.name} (${ch.type})` })),
          }}
        />
      </Modal>
    </div>
  );
}
